/* eslint-disable no-console */
import { AnalyticsBrowser } from '@segment/analytics-next';
import { init as engagementInit } from '@amplitude/engagement-browser';
import * as sessionReplay from '@amplitude/session-replay-browser';
import type { Analytics } from '@segment/analytics-next';
import type {
  GuidesAndSurveysTheme,
  IdentifyTraits,
  PageParams,
  SimpleEventProperties
} from './types';

let analyticsInstance: Analytics | null = null;
let isEngagementInitialized = false;
let storedAmplitudeKey: string | null = null;
let isSessionReplayActive = false;
const SAMPLE_RATE = 0.1;

export const isReady = (): boolean => {
  return analyticsInstance !== null;
};

export const setGuidesAndSurveysTheme = (theme: GuidesAndSurveysTheme) => {
  if (!isEngagementInitialized) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!window?.engagement?.setThemeMode) {
    return;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.engagement.setThemeMode(theme);
  } catch (error) {
    // Silently fail if engagement SDK is not fully initialized
    console.warn('Failed to set guides and surveys theme:', error);
  }
};

const DEFAULT_CDN_URL = 'https://cdn.segment.com';

export const loadWithGuidesAndSurveys = ({
  segmentKey,
  amplitudeKey,
  cdnUrl = DEFAULT_CDN_URL
}: {
  segmentKey: string;
  amplitudeKey: string;
  cdnUrl?: string;
}) => {
  if (isReady()) {
    console.warn('Analytics already initialized');

    return;
  }

  AnalyticsBrowser.load({ writeKey: segmentKey, cdnURL: cdnUrl })
    .then(async ([analytics]) => {
      analyticsInstance = analytics;
      storedAmplitudeKey = amplitudeKey;
      engagementInit(amplitudeKey, { serverZone: 'US' });

      await analytics.ready(async () => {
        const deviceId = analytics.user().anonymousId();

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window?.engagement?.boot) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          await window.engagement.boot({
            user: {
              device_id: deviceId
            },
            integrations: [
              {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                track: async (event: any) => {
                  try {
                    await analytics.track(event.event_type, event.event_properties);
                  } catch (error) {
                    console.error('Failed to track event:', error);
                  }
                }
              }
            ]
          });

          isEngagementInitialized = true;

          setGuidesAndSurveysTheme(
            window?.sessionStorage?.getItem('theme') === 'dark' ? 'darkMode' : 'lightMode'
          );
        }
      });
    })
    .catch((error) => {
      console.error('Failed to initialize analytics:', error);
    });
};

export const identify = (userId: string, traits: IdentifyTraits): void => {
  if (!analyticsInstance) {
    return;
  }

  analyticsInstance.identify(userId, traits).catch((error) => {
    console.error('Failed to identify user:', error);
  });
};

const isPascalCase = (value: string): boolean => /^[A-Z][a-zA-Z0-9]*$/.test(value);

const isSnakeCase = (value: string): boolean => /^[a-z][a-z0-9_]*$/.test(value);

export const track = (event: string, properties?: SimpleEventProperties): void => {
  if (!analyticsInstance) {
    return;
  }

  if (!isPascalCase(event)) {
    console.warn(
      `[analytics] Event name "${event}" is not PascalCase Event was not sent. Expected format: "MyEventName".`
    );

    return;
  }

  if (properties) {
    const invalidKeys = Object.keys(properties).filter((key) => !isSnakeCase(key));

    if (invalidKeys.length > 0) {
      console.warn(
        `[analytics] Property keys must be snake_case Event was not sent. Invalid keys: ${invalidKeys.join(', ')}`
      );

      return;
    }
  }

  const propertiesWithUrl = {
    ...properties,
    url: window?.location?.href
  };

  analyticsInstance.track(event, propertiesWithUrl).catch((error) => {
    console.error('Failed to track event:', error);
  });
};

export const reset = (): void => {
  if (!analyticsInstance) {
    return;
  }

  shutdownSessionReplay();

  // Reset only when we have an identified user
  if (analyticsInstance.user().id()) {
    analyticsInstance.reset();
  }
};

export const page = ({ url, path }: PageParams): void => {
  if (!analyticsInstance) {
    return;
  }

  analyticsInstance.page({ url, path }).catch((error) => {
    console.error('Failed to track page:', error);
  });
};

export const isIdentified = (): boolean => {
  return Boolean(analyticsInstance?.user().id());
};

const shutdownSessionReplay = (): void => {
  try {
    isSessionReplayActive = false;
    sessionReplay.shutdown();
  } catch (error) {
    console.warn('Failed to shutdown session replay:', error);
  }
};

export const startSessionReplay = (): void => {
  if (isSessionReplayActive || !analyticsInstance || !storedAmplitudeKey) {
    return;
  }

  const deviceId = analyticsInstance.user().anonymousId();

  if (!deviceId) {
    return;
  }

  isSessionReplayActive = true;

  sessionReplay
    .init(storedAmplitudeKey, {
      deviceId,
      sessionId: Date.now(),
      sampleRate: SAMPLE_RATE
    })
    .promise.catch((error) => {
      isSessionReplayActive = false;
      console.warn('Failed to initialize session replay:', error);
    });
};
